import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Plus, AlertTriangle, X, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { deleteCustomAdventure, getBusinessDetails } from '@/utils/businessAppApi';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Import extracted components and utilities
import { AdventureBuilderAdminProps, CustomAdventure } from './adventure-builder/types';
import { useAdventureForm } from './adventure-builder/hooks/useAdventureForm';
import { Stepper } from './adventure-builder/components/Stepper';
import { StepOne } from './adventure-builder/steps/StepOne';
import { SimplifiedStepTwo } from './adventure-builder/steps/SimplifiedStepTwo';
import { StepThree } from './adventure-builder/steps/StepThree';
import { EnhancedAvailabilityCalendar } from './EnhancedAvailabilityCalendar';

// Main AdventureBuilderAdmin component

export function AdventureBuilderAdmin({ onAdventureCreated, adventureToEdit, onEditComplete }: AdventureBuilderAdminProps) {
  const [step, setStep] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adventureToDelete, setAdventureToDelete] = useState<CustomAdventure | null>(null);
  const [businessImages, setBusinessImages] = useState<string[]>([]);

  // Use the custom hook for form management
  const formState = useAdventureForm({ onAdventureCreated, adventureToEdit, onEditComplete });

  // Reset step when editing changes
  React.useEffect(() => {
    setStep(1);
  }, [adventureToEdit]);

  // Fetch business images
  React.useEffect(() => {
    const fetchBusinessImages = async () => {
      try {
        const response = await getBusinessDetails();
        const images = response.data.images?.gallery || [];
        setBusinessImages(images);
      } catch (error) {
        console.error('Error fetching business images:', error);
      }
    };
    fetchBusinessImages();
  }, []);

  const confirmDelete = async () => {
    if (!adventureToDelete) return;
    
    try {
      await deleteCustomAdventure(adventureToDelete.id);
      toast.success('Adventure deleted successfully');
    } catch (error) {
      toast.error('Failed to delete adventure');
    } finally {
      setDeleteConfirmOpen(false);
      setAdventureToDelete(null);
    }
  };

  const handleFinish = async () => {
    const success = await formState.handleFinish();
    if (success) {
      if (onEditComplete) {
        onEditComplete();
      }
    }
  };

  const resetForm = () => {
    setStep(1);
    formState.resetForm();
  };

  // Define wizard steps
  const steps = [
    { number: 1, label: 'Adventure Details' },
    { number: 2, label: 'Options & Pricing' },
    { number: 3, label: 'Availability' },
    { number: 4, label: 'Preview' }
  ];

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepOne
            adventureName={formState.adventureName}
            setAdventureName={formState.setAdventureName}
            adventureDescription={formState.adventureDescription}
            setAdventureDescription={formState.setAdventureDescription}
            basePrice={formState.basePrice}
            setBasePrice={formState.setBasePrice}
            basePriceType={formState.basePriceType}
            setBasePriceType={formState.setBasePriceType}
            currency={formState.currency}
            setCurrency={formState.setCurrency}
            adventureImage={formState.images?.[0]}
            setAdventureImage={(imageUrl) => {
              formState.setImages(imageUrl ? [imageUrl] : []);
            }}
            businessImages={businessImages}
            onNext={() => setStep(2)}
            onCancel={() => {
              if (onEditComplete) {
                onEditComplete();
              }
            }}
          />
        );
      case 2:
        return (
          <SimplifiedStepTwo
            currency={formState.currency}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
            // Pass real API options for editing existing adventures
            options={formState.options}
            setOptions={formState.setOptions}
            // Keep simplified options for backward compatibility
            coreOptions={formState.coreOptions || []}
            setCoreOptions={formState.setCoreOptions || (() => {})}
            additionalOptions={formState.additionalOptions || []}
            setAdditionalOptions={formState.setAdditionalOptions || (() => {})}
          />
        );
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-900">Set Adventure Availability</h3>
              <p className="text-sm text-slate-600 mt-1">
                Configure when your adventure is available for booking
              </p>
            </div>
            
            <EnhancedAvailabilityCalendar
              adventureId={formState.editingAdventure?.id || 'new'}
              adventureName={formState.adventureName}
              onAvailabilityUpdate={(availability: any) => {
                // Handle availability update if needed
                console.log('Availability updated:', availability);
              }}
            />
            
            <div className="flex justify-between pt-6">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex items-center gap-2"
              >
                ← Back to Options
              </Button>
              <Button
                onClick={() => setStep(4)}
                className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
              >
                Continue to Preview →
              </Button>
            </div>
          </div>
        );
      case 4:
        return (
          <StepThree
            adventureName={formState.adventureName}
            adventureDescription={formState.adventureDescription}
            basePrice={formState.basePrice}
            basePriceType={formState.basePriceType}
            currency={formState.currency}
            options={formState.options}
            isSubmitting={formState.isSubmitting}
            onBack={() => setStep(3)}
            onFinish={handleFinish}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Direct Content - No Dialog Wrapper */}
      <div className="px-6 py-8 bg-white">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Modern Stepper */}
          <div className="relative">
            <Stepper currentStep={step} steps={steps} />
          </div>
          
          {/* Step Content */}
          <div className="bg-slate-50/50 rounded-xl p-6 border border-slate-200/60">
            {renderStep()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[500px] border-slate-200/60 shadow-xl shadow-slate-900/10">
          <DialogHeader className="pb-6 border-b border-slate-100/80">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-red-200 border border-red-200/50 flex items-center justify-center shadow-sm">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-slate-900">
                  Confirm Deletion
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  This action cannot be undone
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="py-6">
            <div className="p-4 rounded-lg bg-red-50 border border-red-200/50">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900 mb-1">
                    Delete "{adventureToDelete?.name}"?
                  </p>
                  <p className="text-sm text-red-700">
                    This will permanently remove the adventure and all its configurations. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-6 border-t border-slate-200/60">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-shadow"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Delete Adventure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 